/**
 * Albums, Artists, Genres and Folders: the library grouped the ways people
 * browse music. Each card or row opens the songs behind it; each has a ⋯ menu
 * to play or queue all of them.
 */

import * as searchParse from "../search/parse";
import { useEffect, useMemo, useState } from "react";
import { createFolder, deleteFolder, engineBase, setFolderManualOnly, uploadSongs } from "../api";
import { useApp } from "../core/app";
import { bus } from "../core/bus";
import { player } from "../core/player";
import type { MenuItem } from "../core/registry";
import { LibraryTree } from "../LibraryTree";
import { Artwork } from "../shell/Artwork";
import { Icon } from "../shell/Icons";
import { MoreButton } from "../shell/PopupMenu";
import { toast } from "../shell/Toast";
import { clockTime } from "../shell/Waveform";
import type { Track } from "../types";

const songs = (n: number) => `${n.toLocaleString()} song${n === 1 ? "" : "s"}`;

/**
 * Copy songs dropped from the computer into a library folder (on the machine
 * Nova runs on), then refresh. Used by the Folders tree and by Songs while it
 * shows one folder.
 */
export async function copyInto(
  refresh: () => Promise<unknown> | void,
  target: { rootId: number; folder: string; label: string },
  files: File[],
) {
  if (!files.length) return;
  toast(`Copying ${songs(files.length)} into “${target.label}”…`, "ok");
  const { copied, failed } = await uploadSongs(target.rootId, target.folder, files);
  if (copied.length) toast(`Copied ${songs(copied.length)} into “${target.label}”. They are being analysed.`, "ok");
  for (const f of failed.slice(0, 3)) toast(`“${f.name}” was not copied: ${f.error}.`, "bad");
  if (failed.length > 3) toast(`${failed.length - 3} more were not copied.`, "bad");
  await refresh();
}

interface Album {
  name: string;
  artist: string;
  year: number | null;
  tracks: number;
  seconds: number;
  trackId: string;
}

interface Artist {
  name: string;
  tracks: number;
  trackId?: string;
}

function useEngineList<T>(path: string, key: string, deps: unknown[]): T[] | null {
  const [rows, setRows] = useState<T[] | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => bus.on("library.changed", () => setTick((n) => n + 1)), []);
  useEffect(() => {
    let live = true;
    fetch(`${engineBase()}${path}`)
      .then((r) => r.json())
      .then((body) => live && setRows((body[key] as T[]) ?? []))
      .catch(() => live && setRows([]));
    return () => {
      live = false;
    };
  }, [path, tick, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps
  return rows;
}

/** Fetch every song matching a search, for "play all" on an album or artist. */
async function songsFor(query: string): Promise<Track[]> {
  const { parse } = searchParse;
  const ast = parse(query);
  const res = await fetch(`${engineBase()}/api/tracks?limit=1000&q=${encodeURIComponent(JSON.stringify({ clauses: ast.clauses, free: ast.free }))}`);
  const body = (await res.json()) as { tracks?: Track[] };
  return body.tracks ?? [];
}

function groupMenu(label: string, query: string, open: () => void): MenuItem[] {
  const run = (fn: (t: Track[]) => void, done: (n: number) => string) => async () => {
    const tracks = await songsFor(query);
    if (!tracks.length) return toast("No playable songs found.", "bad");
    fn(tracks);
    toast(done(tracks.length), "ok");
  };
  return [
    { id: "play", label: "Play", icon: "play", run: run((t) => player.playTracks(t), (n) => `Playing ${label} · ${songs(n)}.`) },
    { id: "shuffle", label: "Shuffle", icon: "shuffle", run: run((t) => { player.setShuffle(true); player.playTracks(t, Math.floor(Math.random() * t.length)); }, (n) => `Shuffling ${songs(n)}.`) },
    { id: "next", label: "Play next", icon: "next", run: run((t) => player.playNext(t), (n) => n === 1 ? "1 song plays next." : `${n} songs play next.`) },
    { id: "queue", label: "Add to queue", icon: "queue", run: run((t) => player.enqueue(t), (n) => `Added ${songs(n)} to the queue.`) },
    { id: "open", group: "b", label: "Show songs", icon: "list", run: open },
  ];
}

const q = (s: string) => `"${s.replace(/"/g, "")}"`;

export function AlbumsView() {
  const app = useApp();
  const [filter, setFilter] = useState("");
  const albums = useEngineList<Album>("/api/albums?limit=5000", "albums", []);
  const shown = useMemo(() => (albums ?? []).filter((a) => !filter || `${a.name} ${a.artist}`.toLowerCase().includes(filter.toLowerCase())), [albums, filter]);
  const open = (a: Album) => {
    app.setInput(`album:${q(a.name)}`);
    app.navigate("songs");
  };
  return (
    <section className="nv-view" aria-label="Albums">
      <div className="nv-viewhead">
        <div className="nv-viewtitle">
          <h1>Albums</h1>
          <div className="nv-sub">{albums ? `${albums.length.toLocaleString()} albums` : "Loading…"}</div>
        </div>
        <div className="nv-actions">
          <input className="nv-input" type="search" placeholder="Filter albums" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter albums" />
        </div>
      </div>
      <div className="nv-cardgrid" role="list">
        {shown.map((a) => (
          <div key={`${a.name}\u0000${a.artist}`} className="nv-card" role="listitem">
            <button className="nv-card-art" onClick={() => open(a)} title={`Open ${a.name}`}>
              <Artwork trackId={a.trackId} seed={a.name} rounded={8} />
              <span className="nv-card-play" aria-hidden="true"><Icon name="play" /></span>
            </button>
            <div className="nv-card-text">
              <b title={a.name}>{a.name}</b>
              <span title={a.artist}>{a.artist || "Various artists"}{a.year ? ` · ${a.year}` : ""}</span>
              <span className="nv-dim">{songs(a.tracks)} · {clockTime(a.seconds)}</span>
            </div>
            <MoreButton className="nv-card-more" label={`Options for ${a.name}`} items={() => groupMenu(`“${a.name}”`, `album:${q(a.name)}`, () => open(a))} />
          </div>
        ))}
        {albums && !shown.length && <div className="nv-empty">No albums{filter ? " match" : " yet — songs need an album tag"}.</div>}
      </div>
    </section>
  );
}

export function ArtistsView() {
  const app = useApp();
  const [filter, setFilter] = useState("");
  const artists = useEngineList<Artist>(`/api/artists?limit=1000&q=${encodeURIComponent(filter)}`, "artists", []);
  const open = (a: Artist) => {
    app.setInput(`artist:${q(a.name)}`);
    app.navigate("songs");
  };
  return (
    <section className="nv-view" aria-label="Artists">
      <div className="nv-viewhead">
        <div className="nv-viewtitle">
          <h1>Artists</h1>
          <div className="nv-sub">{artists ? `${artists.length.toLocaleString()} artists, featured ones included` : "Loading…"}</div>
        </div>
        <div className="nv-actions">
          <input className="nv-input" type="search" placeholder="Filter artists" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter artists" />
        </div>
      </div>
      <div className="nv-rowlist" role="list">
        {(artists ?? []).map((a) => (
          <div key={a.name} className="nv-row" role="listitem">
            <button className="nv-row-main" onClick={() => open(a)}>
              <Artwork trackId={a.trackId} seed={a.name} size={40} rounded={20} />
              <b title={a.name}>{a.name}</b>
              <span className="nv-dim">{songs(a.tracks)}</span>
            </button>
            <MoreButton label={`Options for ${a.name}`} items={() => groupMenu(a.name, `artist:${q(a.name)}`, () => open(a))} />
          </div>
        ))}
      </div>
    </section>
  );
}

export function GenresView() {
  const app = useApp();
  const genres = useMemo(() => [...app.library.genres].sort((a, b) => b.tracks - a.tracks), [app.library.genres]);
  const open = (name: string) => {
    app.setInput(`genre:${q(name)}`);
    app.navigate("songs");
  };
  return (
    <section className="nv-view" aria-label="Genres">
      <div className="nv-viewhead">
        <div className="nv-viewtitle">
          <h1>Genres</h1>
          <div className="nv-sub">{genres.length} genres in your library · Nova knows 117 African genres and their relatives</div>
        </div>
      </div>
      <div className="nv-genregrid" role="list">
        {genres.map((g) => (
          <div key={g.name} className="nv-genre" role="listitem">
            <button className="nv-genre-main" onClick={() => open(g.name)} title={g.resolved ? `Nova reads this as ${g.resolved}` : "Not in Nova's taxonomy yet"}>
              <b>{g.name}</b>
              <span className="nv-dim">{songs(g.tracks)}{g.resolved && g.resolved !== g.name ? ` · ${g.resolved}` : ""}</span>
            </button>
            <MoreButton label={`Options for ${g.name}`} items={() => groupMenu(g.name, `genre:${q(g.name)}`, () => open(g.name))} />
          </div>
        ))}
        {!genres.length && <div className="nv-empty">No genres yet. They come from your files' genre tags.</div>}
      </div>
    </section>
  );
}

export function FoldersView() {
  const app = useApp();
  const [active, setActive] = useState<string | null>(null);
  return (
    <section className="nv-view" aria-label="Folders">
      <div className="nv-viewhead">
        <div className="nv-viewtitle">
          <h1>Folders</h1>
          <div className="nv-sub">The music folders Nova reads. Make a folder from a folder's ⋯ menu; drop songs from your computer onto a folder to copy them in. Nothing else is ever written into them except tags you edit, and nothing is replaced.</div>
        </div>
      </div>
      <div className="nv-folders">
        <LibraryTree
          roots={app.library.tree}
          activePath={active}
          onPick={(node) => {
            const id = `${node.rootId ?? node.id}:${node.path ?? ""}`;
            setActive(id);
            void app.library.setScope({ rootId: node.rootId ?? node.id, folder: node.kind === "root" ? undefined : node.path, label: node.name });
            app.navigate("songs");
          }}
          onAction={async (node, action) => {
            if (action === "manual") {
              const on = !node.manualOnly;
              const out = await setFolderManualOnly(node.rootId ?? 0, node.path ?? "", on);
              if (out.error) return toast(out.error, "bad");
              toast(on
                ? `Songs in “${node.name}” are hand-picked only: Auto DJ and Flow leave them out.`
                : `Songs in “${node.name}” can be picked by Auto DJ and Flow again.`, "ok");
              await app.library.refresh();
              return;
            }
            if (action === "delete") {
              if (!window.confirm(`Delete the folder “${node.name}” from disk?\n\nOnly an empty folder can be deleted.`)) return;
              const out = await deleteFolder(node.rootId ?? 0, node.path ?? "");
              if (out.error) return toast(out.error, "bad");
              toast(`Deleted the folder “${node.name}”.`, "ok");
              await app.library.refresh();
              return;
            }
            if (action === "remove" && !window.confirm(`Remove “${node.name}” from the library?\n\nIts songs are archived, not deleted. Re-adding the folder brings everything back.`)) return;
            if (action === "archive" && !window.confirm(`Archive everything in “${node.name}”?\n\nThe songs leave the library view and stay findable with archived:yes.`)) return;
            const body = action === "archive" ? { rootId: node.rootId ?? node.id, folder: node.path, archived: true } : { rootId: node.rootId ?? node.id };
            const error = await app.library.folderAction(action, body);
            if (error) toast(error, "bad");
          }}
          onCreate={async (node, name) => {
            const out = await createFolder(node.rootId ?? node.id ?? 0, node.kind === "root" ? "" : node.path ?? "", name);
            if (out.error) {
              toast(out.error, "bad");
              return out.error;
            }
            toast(`Made the folder “${name}”. Drop songs on it to copy them in.`, "ok");
            await app.library.refresh();
            return null;
          }}
          onDropFiles={(node, files) => void copyInto(app.library.refresh, {
            rootId: node.rootId ?? node.id ?? 0, folder: node.kind === "root" ? "" : node.path ?? "", label: node.name,
          }, files)}
        />
        {!app.library.tree.length && <div className="nv-empty">No folders yet. Use File → Add music folder…</div>}
      </div>
    </section>
  );
}
